import _ from 'lodash'
import { getReviewRatingValue } from '../../utils/misc'
import { ReflectionPickerDataQueryResult } from '@graphql/queries/__generated__/product-details.generated'
import { SITE_NAME } from '../../../constants'

type TSchemaAvailability =
  | 'https://schema.org/Discontinued'
  | 'https://schema.org/InStock'
  | 'https://schema.org/InStoreOnly'
  | 'https://schema.org/LimitedAvailability'
  | 'https://schema.org/OnlineOnly'
  | 'https://schema.org/OutOfStock'
  | 'https://schema.org/PreOrder'
  | 'https://schema.org/PreSale'
  | 'https://schema.org/SoldOut'

interface ISchemaOffer {
  '@type': 'Offer'
  url: string
  price: number | string
  priceCurrency: string
  sku: string
  itemCondition: string
  priceValidUntil: Date | string
  availability: TSchemaAvailability
}

interface ISchemaAggregateOffer {
  '@type': 'AggregateOffer'
  highPrice: number
  lowPrice: number
  offerCount: number
  priceCurrency: string
  offers: ISchemaOffer[]
}

interface ISchemaReview {
  '@type': 'Review'
  datePublished: Date | string
  reviewBody: string
  name: string
  reviewRating: {
    '@type': 'Rating'
    ratingValue: number
    bestRating: number
    worstRating: number
  }
  author: {
    '@type': 'Person'
    name: string
  }
}

interface ISchemaBrand {
  '@type': 'Brand'
  name: string
}

interface ISchemaAggregateRating {
  '@type': 'AggregateRating'
  ratingValue: number
  reviewCount: number
}

export interface ISchemaProductStructuredData {
  '@context': 'https://schema.org/'
  '@type': string
  name: string
  image: string[]
  description: string
  url: string
  sku: number | string
  isbn: string | number
  upc: string | number
  ean: string | number
  brand: ISchemaBrand
  review?: ISchemaReview[] | ISchemaReview
  aggregateRating?: ISchemaAggregateRating
  offers: ISchemaAggregateOffer
}

const getReviewList = (
  review: ReflectionPickerDataQueryResult['data']['reflectionDetails']['reflection']['product']['feedbacks']
): ISchemaReview[] => {
  if (!review) return []

  return review.results.map(node => {
    return {
      '@type': 'Review',
      name: node.title || node.userName,
      reviewBody: node.comment,
      datePublished: node.timeCreated ? new Date(node.timeCreated).toISOString() : '',
      author: {
        '@type': 'Person',
        name: node.userName,
      },
      reviewRating: {
        '@type': 'Rating',
        bestRating: 5,
        worstRating: 1,
        ratingValue: getReviewRatingValue(node.score),
      },
    }
  })
}

const getVariantsStructuredData = (
  product: ReflectionPickerDataQueryResult['data']['reflectionDetails']['reflection']['product'],
  variants: ReflectionPickerDataQueryResult['data']['reflectionDetails']['reflection']['product']['variants']
): ISchemaAggregateOffer => {
  const inStock = 'https://schema.org/InStock'
  const outOfStock = 'https://schema.org/OutOfStock'

  return {
    '@type': 'AggregateOffer',
    lowPrice: product.priceRange.discount.min,
    highPrice: product.priceRange.discount.max,
    offerCount: variants.length,
    priceCurrency: product.priceRange.currency,
    offers: variants?.map(variant => ({
      '@type': 'Offer',
      url: location.href,
      price: variant.priceRange.discount.min.toFixed(2) || 0,
      priceCurrency: variant.priceRange.currency,
      sku: variant.sku,
      itemCondition: 'https://schema.org/NewCondition',
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString(),
      availability:
        variant.stockQuantity > 0 || ['IN', 'BACKORDER'].includes(variant.outOfStockStatus) || !variant.shippingRequired
          ? inStock
          : outOfStock,
    })),
  }
}

export const structuredData = (
  reflection: ReflectionPickerDataQueryResult['data']['reflectionDetails']['reflection'] | any,
  imagesUrls
) => {
  if (!reflection || !imagesUrls) return null
  const product = reflection.product
  const data: ISchemaProductStructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: reflection.name,
    image: imagesUrls.map(url => `${location.origin}${url}`),
    description: product.metaDescription || reflection.description.replace(/<[^>]+>/g, ''),
    url: location.href,
    sku: product.sku,
    isbn: product.isbn,
    upc: product.upc,
    ean: product.ean,
    brand: {
      '@type': 'Brand',
      name: product.manufacturer?.name || SITE_NAME,
    },

    offers: getVariantsStructuredData(product, product.variants || []),
  }
  // if (product.feedbacks?.totalCount > 0) {
  //   data.aggregateRating = {
  //     '@type': 'AggregateRating',
  //     ratingValue:
  //       _.sumBy(product.feedbacks.edges, (edge: any) => getReviewRatingValue(edge.node.score)) /
  //       product.feedbacks.edges.length,
  //     reviewCount: product.feedbacks.totalCount || 1,
  //   }
  //   data.review = getReviewList(product.feedbacks)
  // }
  return JSON.stringify(data)
}
